import { immerable } from 'immer'
import { isArray } from 'lodash-es'
import EventEmitter3 from 'eventemitter3'

import {
  AbstractNode,
  ElementIdentifier,
  LayoutNodeData,
  NodeData,
  NODE_EVENT_NAME,
} from '../types'
import { EVENT_NAME } from '../types'
import type { Graph } from './graph'

export class Node implements AbstractNode {
  [immerable] = true

  id: ElementIdentifier
  graph: Graph
  eventEmitter: EventEmitter3
  fx: NodeData['fx']
  fy: NodeData['fy']
  size: NonNullable<NodeData['size']>
  color: NodeData['color']
  animate: NodeData['animate']
  data: NodeData['data']
  label: NodeData['label']
  states: string[] = []
  element?: HTMLElement | SVGElement
  type?: NodeData['type']
  mergedNodes?: NodeData[]

  static DEFAULT_NODE_SIZE: [number, number] = [60, 60]

  /**
   * 节点 x 坐标
   */
  get x() {
    return this.graph.positions[this.id]?.[0] ?? 0
  }

  /**
   * 节点 y 坐标
   */
  get y() {
    return this.graph.positions[this.id]?.[1] ?? 0
  }

  /**
   * 节点所有起始边
   */
  get sourceEdges() {
    return this.graph.edges.filter((edge) => edge.source === this.id)
  }

  /**
   * 节点所有目标边
   */
  get targetEdges() {
    return this.graph.edges.filter((edge) => edge.target === this.id)
  }

  /**
   * 节点关联的所有边(入边和出边)
   */
  get allEdges() {
    return this.graph.edges.filter(({ source, target }) =>
      [source, target].includes(this.id)
    )
  }

  constructor(graph: Graph, nodeData: NodeData) {
    const { id, data, label, x, y, type, size, color, animate, ...rest } =
      nodeData

    this.graph = graph
    this.id = id
    this.data = data
    this.label = label
    this.size = size ?? Node.DEFAULT_NODE_SIZE
    this.color = color
    this.animate = animate
    this.type = type
    this.mergedNodes = nodeData.mergedNodes
    this.eventEmitter = new EventEmitter3()

    Object.assign(this, rest)
  }

  /**
   * 是否为节点
   *
   * @param node
   */
  static isNode(node: ElementIdentifier | Node): node is Node {
    return node instanceof Node
  }

  /**
   * 设置节点元素
   *
   * @param element
   */
  setElement(element: HTMLElement | SVGElement) {
    this.graph.service.send({
      type: 'SET_NODE_ELEMENT',
      data: {
        id: this.id,
        element,
        eventName: EVENT_NAME['node:mount'],
      },
    })
  }

  /**
   * 获取布局数据
   */
  toLayoutData(): LayoutNodeData {
    const { id, x, y, size, fx, fy } = this

    return {
      id,
      x,
      y,
      fx,
      fy,
      size,
    }
  }

  /**
   * 设置状态列表
   *
   * @param states
   */
  setStates(states: string[]) {
    this.graph.service.send({
      type: 'UPDATE_NODE_PROPERTIES',
      data: {
        id: this.id,
        nodeData: {
          states,
        },
      },
    })
  }

  /**
   * 设置单个状态
   *
   * @param state
   * @param enable
   */
  setState(state: string, enable = true) {
    if (enable) {
      if (!this.states.includes(state)) {
        this.setStates([...this.states, state])
      }
    } else {
      this.setStates(this.states.filter((val) => val !== state))
    }
  }

  /**
   * 取消状态
   *
   * @param states
   */
  clearStates(states?: string | string[]) {
    if (states) {
      states = isArray(states) ? states : [states]

      this.setStates(this.states.filter((state) => !states!.includes(state)))
    } else {
      this.setStates([])
    }
  }

  /**
   * 是否包含指定状态
   *
   * @param state
   * @returns
   */
  hasState(state: string) {
    return this.states.includes(state)
  }

  /**
   * 切换状态
   *
   * @param state
   */
  toggleState(state: string) {
    if (this.hasState(state)) {
      this.setStates(this.states.filter((val) => val !== state))
    } else {
      this.setStates([...this.states, state])
    }
  }

  /**
   * 独占某个状态
   *
   * @state 状态
   * @list 其他需要同状态的节点, 为空时则表示当前元素独占该状态
   */
  exclusiveState(state: string, list?: ElementIdentifier[]) {
    this.graph.service.send({
      type: 'SET_NODE_EXCLUSIVE_STATE',
      data: {
        id: this.id,
        state,
        list,
      },
    })
  }

  /**
   * 派发位置更新事件
   */
  emitPositionUpdate = () => {
    this.eventEmitter.emit(NODE_EVENT_NAME.onPositionUpdate)
  }

  /**
   * 更新事件
   */
  onPositionUpdate = (callback: () => void) => {
    this.eventEmitter.on(NODE_EVENT_NAME.onPositionUpdate, callback)

    return () => {
      this.eventEmitter.removeListener(
        NODE_EVENT_NAME.onPositionUpdate,
        callback
      )
    }
  }
}
